'use client';

import React, { useCallback, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useSignIn } from '@clerk/nextjs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ArrowLeft, Lock, Mail, RefreshCw } from 'lucide-react';

type Step = 'request' | 'reset';

export default function ResetPasswordPage() {
  const router = useRouter();
  const { isLoaded, signIn, setActive } = useSignIn();

  const [step, setStep] = useState<Step>('request');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleRequest = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded) return;
    setSubmitting(true);
    setError(null);
    try {
      await signIn.create({
        strategy: 'reset_password_email_code',
        identifier: email.trim(),
      });
      setStep('reset');
    } catch (err: any) {
      const msg = err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? 'Failed to send reset code';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [isLoaded, signIn, email]);

  const handleReset = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || code.trim().length < 6 || newPassword.length < 8) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: 'reset_password_email_code',
        code: code.trim(),
        password: newPassword,
      });
      if (result.status === 'complete') {
        await setActive({ session: result.createdSessionId });
        router.push('/');
      } else if (result.status === 'needs_second_factor') {
        // Reset succeeded but account has MFA — kick over to sign-in to
        // complete the second factor. Rare case.
        router.push('/auth/login');
      } else {
        setError('Reset incomplete. Please try again.');
      }
    } catch (err: any) {
      const msg = err?.errors?.[0]?.longMessage ?? err?.errors?.[0]?.message ?? 'Reset failed';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }, [isLoaded, signIn, setActive, code, newPassword, router]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src="/assets/brand/Outhere_logo_black.png" alt="OutThere" className="inline-block h-[125px] w-[250px] object-contain dark:hidden" />
          <img src="/assets/brand/Outhere_logo_white.png" alt="OutThere" className="hidden dark:inline-block h-[125px] w-[250px] object-contain" />
        </div>

        <Card>
          {step === 'request' ? (
            <>
              <CardHeader>
                <CardTitle>Reset your password</CardTitle>
                <CardDescription>
                  Enter your email and we&apos;ll send you a 6-digit code.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRequest} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }}
                        placeholder="you@example.com"
                        autoComplete="email"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <Button type="submit" disabled={submitting || !isLoaded} className="w-full">
                    {submitting ? (
                      <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Sending…</>
                    ) : 'Send reset code'}
                  </Button>
                </form>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader>
                <CardTitle>Enter your new password</CardTitle>
                <CardDescription>
                  We sent a code to <strong>{email}</strong>. Enter it below with your new password.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleReset} className="space-y-4">
                  {error && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="code">Verification code</Label>
                    <Input
                      id="code"
                      value={code}
                      onChange={(e) => { setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); if (error) setError(null); }}
                      placeholder="123456"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="newPassword">New password</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="newPassword"
                        type="password"
                        value={newPassword}
                        onChange={(e) => { setNewPassword(e.target.value); if (error) setError(null); }}
                        placeholder="At least 8 characters"
                        autoComplete="new-password"
                        minLength={8}
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting || !isLoaded || code.length < 6 || newPassword.length < 8}
                    className="w-full bg-red-500 hover:bg-red-600"
                  >
                    {submitting ? (
                      <><RefreshCw className="mr-2 h-4 w-4 animate-spin" />Resetting…</>
                    ) : 'Reset password'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => { setStep('request'); setCode(''); setNewPassword(''); setError(null); }}
                    className="w-full text-muted-foreground hover:text-foreground"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" /> Use a different email
                  </Button>
                </form>
              </CardContent>
            </>
          )}
          <CardFooter className="flex flex-col items-center space-y-2 pt-0">
            <div className="text-sm text-muted-foreground">
              Remembered it?{' '}
              <Link href="/auth/login" className="text-blue-600 hover:text-blue-800 font-medium">Sign in</Link>
            </div>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
